import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import LiveLayout from "./LiveLayout";
import api from "@/lib/api";
import EntityLogo from "@/components/EntityLogo";
import { toast } from "sonner";
import {
  Radio, Video, CheckCircle2, Flame, Star, CalendarClock, Megaphone,
  ChevronRight, Shield, ArrowRight, Copy, Share2,
} from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function LiveHome() {
  const [feed, setFeed] = useState(null);
  useEffect(() => { api.get("/live/feed").then(({ data }) => setFeed(data)).catch(() => setFeed({})); }, []);

  return (
    <LiveLayout>
      {/* Hero */}
      <div className="bg-[#0A192F] text-white">
        <div className="max-w-3xl mx-auto px-5 py-8">
          <div className="inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.2em] text-[#E63946]">
            <span className="w-2 h-2 rounded-full bg-[#E63946] animate-pulse" /> CBR Live
          </div>
          <h1 data-testid="live-home-title" className="font-display text-3xl sm:text-4xl uppercase tracking-tight mt-2">The Heartbeat of Canadian Baseball</h1>
          <p className="text-sm text-white/60 mt-2">Live games, scores, and the players moving across the CBR ecosystem — all in one feed.</p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-5 py-6 space-y-8" data-testid="live-home-feed">
        {!feed ? <div className="py-16 text-center text-sm text-slate-400">Loading feed…</div> : <>
          <StreamSection items={feed.live_streams} />
          <GameSection title="Live Games" icon={Radio} items={feed.live_games} live emptyText="No games are live right now." testid="section-live-games" />
          <GameSection title="Final Scores" icon={CheckCircle2} items={feed.final_scores} emptyText="No completed games yet." testid="section-final-scores" />
          <PlayerSection title="Trending Players" icon={Flame} items={feed.trending_players} testid="section-trending-players"
            emptyText="Trending players appear as activity grows." />
          <FollowSection items={feed.teams_you_follow} />
          <GameSection title="Upcoming Games" icon={CalendarClock} items={feed.upcoming_games} upcoming emptyText="No upcoming games scheduled." testid="section-upcoming-games" />
          <PlayerSection title="Recruiting Headlines" icon={Megaphone} items={feed.recruiting_headlines} testid="section-recruiting-headlines"
            renderMeta={(p) => p.school_name} emptyText="No recent commitments yet." cta={{ to: "/movers", label: "See Movers & Risers" }} />
        </>}
      </div>
    </LiveLayout>
  );
}

function SectionHead({ icon: Icon, title, cta }) {
  return (
    <div className="flex items-center justify-between mb-3">
      <div className="flex items-center gap-2">
        <Icon className="w-4 h-4 text-[#E63946]" />
        <h2 className="font-display text-lg uppercase tracking-tight text-[#0A192F]">{title}</h2>
      </div>
      {cta && <Link to={cta.to} className="text-xs font-semibold text-slate-500 hover:text-[#E63946] flex items-center gap-1">{cta.label} <ChevronRight className="w-3.5 h-3.5" /></Link>}
    </div>
  );
}
const Empty = ({ children }) => <div className="bg-white border border-dashed border-slate-200 rounded-lg py-6 text-center text-sm text-slate-400">{children}</div>;

function StreamSection({ items = [] }) {
  if (!items.length) return null;
  return (
    <section data-testid="section-live-streams">
      <SectionHead icon={Video} title="Live Streams" />
      <div className="flex gap-3 overflow-x-auto pb-1">
        {items.map((g) => (
          <Link key={g.id} to={`/live/games/${g.id}`} className="min-w-[240px] bg-[#0A192F] text-white rounded-lg overflow-hidden shrink-0 hover:ring-2 ring-[#E63946]">
            <div className="aspect-video bg-gradient-to-br from-[#122543] to-[#0A192F] grid place-items-center relative">
              <Video className="w-8 h-8 text-white/30" />
              <span className="absolute top-2 left-2 text-[9px] font-bold bg-[#E63946] px-1.5 py-0.5 rounded-sm">● LIVE</span>
            </div>
            <div className="p-3 text-xs"><div className="font-semibold truncate">{g.team?.name} vs {g.opponent?.name}</div></div>
          </Link>
        ))}
      </div>
    </section>
  );
}

function GameSection({ title, icon, items = [], live, upcoming, emptyText, testid }) {
  return (
    <section data-testid={testid}>
      <SectionHead icon={icon} title={title} />
      {items.length === 0 ? <Empty>{emptyText}</Empty> : (
        <div className="space-y-2">
          {items.map((g) => <GameCard key={g.id} g={g} live={live} upcoming={upcoming} />)}
        </div>
      )}
    </section>
  );
}

function GameCard({ g, live, upcoming }) {
  const isFinal = !live && !upcoming;
  const copyLink = (e) => {
    e.preventDefault(); e.stopPropagation();
    navigator.clipboard?.writeText(`${BACKEND ? "" : ""}${window.location.origin}/live/games/${g.id}`);
    toast.success("Final score link copied");
  };
  const share = async (e) => {
    e.preventDefault(); e.stopPropagation();
    const url = `${window.location.origin}/live/games/${g.id}`;
    const text = `Final: ${g.team?.name} ${g.score?.home ?? 0}, ${g.opponent?.name} ${g.score?.away ?? 0}`;
    if (navigator.share) { try { await navigator.share({ title: "CBR Live", text, url }); } catch (_) {} }
    else { navigator.clipboard?.writeText(`${text} ${url}`); toast.success("Copied to clipboard"); }
  };
  return (
    <Link to={`/live/games/${g.id}`} data-testid={`feed-game-${g.id}`} className="block bg-white border border-slate-200 rounded-lg p-3 hover:border-[#E63946] transition-colors group">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-[11px] text-slate-400 uppercase tracking-wider">
          {live ? <span className="text-[9px] font-bold text-white bg-[#E63946] px-1.5 py-0.5 rounded-sm animate-pulse">● LIVE</span>
            : upcoming ? <span>{(g.date || "").slice(5)} {g.time || ""}</span>
            : <span className="text-slate-500 font-semibold">FINAL</span>}
          <span className="capitalize">· {(g.game_type || "").replace(/_/g, " ")}</span>
        </div>
        <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-[#E63946]" />
      </div>
      <div className="mt-2 space-y-1">
        <TeamLine name={g.team?.name} logo={g.team?.logo_url} score={g.score?.home} show={!upcoming} bold={!upcoming && (g.score?.home >= g.score?.away)} />
        <TeamLine name={g.opponent?.name} logo={g.opponent?.logo_url} score={g.score?.away} show={!upcoming} bold={!upcoming && (g.score?.away > g.score?.home)} />
      </div>
      {live && <div className="text-[11px] text-slate-400 mt-1">{g.half === "top" ? "Top" : "Bot"} {g.inning || 1}</div>}
      {g.location && upcoming && <div className="text-[11px] text-slate-400 mt-1">{g.location}</div>}
      {isFinal && (
        <div className="flex items-center gap-2 mt-2 pt-2 border-t border-slate-100" data-testid={`final-card-actions-${g.id}`}>
          {g.location && <span className="text-[11px] text-slate-400 mr-auto truncate">{g.location}</span>}
          <button onClick={copyLink} data-testid={`final-copy-${g.id}`} className="flex items-center gap-1 text-[11px] font-semibold text-slate-500 hover:text-[#E63946]"><Copy className="w-3 h-3" /> Copy</button>
          <button onClick={share} data-testid={`final-share-${g.id}`} className="flex items-center gap-1 text-[11px] font-semibold text-slate-500 hover:text-[#E63946]"><Share2 className="w-3 h-3" /> Share</button>
        </div>
      )}
    </Link>
  );
}
const TeamLine = ({ name, logo, score, show, bold }) => (
  <div className="flex items-center gap-2">
    <EntityLogo src={logo} alt={name} size="w-6 h-6" textSize="text-[8px]" />
    <span className={`text-sm flex-1 truncate ${bold ? "font-bold text-[#0A192F]" : "text-slate-600"}`}>{name || "TBD"}</span>
    {show && <span className={`font-data text-lg ${bold ? "text-[#0A192F]" : "text-slate-400"}`}>{score ?? 0}</span>}
  </div>
);

function PlayerSection({ title, icon, items = [], renderMeta, emptyText, cta, testid }) {
  return (
    <section data-testid={testid}>
      <SectionHead icon={icon} title={title} cta={cta} />
      {items.length === 0 ? <Empty>{emptyText}</Empty> : (
        <div className="flex gap-3 overflow-x-auto pb-1">
          {items.map((p) => (
            <Link key={p.id || p.slug} to={`/prospects/${p.slug}`} data-testid={`feed-player-${p.slug}`}
              className="min-w-[150px] bg-white border border-slate-200 rounded-lg p-3 shrink-0 hover:border-[#E63946] transition-colors">
              <div className="w-12 h-12 rounded-full bg-slate-100 overflow-hidden grid place-items-center mb-2">
                {p.profile_photo_url ? <img src={p.profile_photo_url} alt="" className="w-full h-full object-cover" /> : <Star className="w-5 h-5 text-slate-300" />}
              </div>
              <div className="text-sm font-semibold text-[#0A192F] truncate">{p.first_name} {p.last_name}</div>
              <div className="text-[11px] text-slate-400 truncate">{renderMeta ? renderMeta(p) : [p.primary_position, p.graduation_year].filter(Boolean).join(" · ")}</div>
            </Link>
          ))}
        </div>
      )}
    </section>
  );
}

function FollowSection({ items = [] }) {
  return (
    <section data-testid="section-teams-you-follow">
      <SectionHead icon={Star} title="Teams You Follow" />
      {items.length === 0 ? (
        <div className="bg-white border border-dashed border-slate-200 rounded-lg py-6 px-4 text-center">
          <p className="text-sm text-slate-400">You're not following any teams yet.</p>
          <Link to="/live/team-rankings" className="inline-flex items-center gap-1 text-xs font-semibold text-[#E63946] mt-2">Browse teams <ArrowRight className="w-3.5 h-3.5" /></Link>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {items.map((t) => (
            <Link key={t.id} to={`/live/teams/${t.slug}`} data-testid={`follow-team-${t.id}`}
              className="bg-white border border-slate-200 rounded-lg p-3 hover:border-[#E63946] transition-colors flex items-center gap-2">
              <EntityLogo src={t.effective_logo_url || t.logo_url} alt={t.name} size="w-9 h-9" textSize="text-[10px]" />
              <div className="min-w-0"><div className="text-sm font-semibold truncate">{t.name}</div>
                <div className="text-[11px] text-slate-400">{t.age_division || ""}</div></div>
            </Link>
          ))}
        </div>
      )}
    </section>
  );
}
